import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import NewCustomDataTable from "../common/NewCustomDataTable.jsx";
import useScrollLoadMore from "../common/useScrollLoadMore";
import useDebouncedCallback from "../common/useDebouncedCallback";
import { useWaypoint } from "../common/waypoint/WaypointProvider.jsx";
import { getCrmReportList } from "../../../services/CrmReportService";
import CrmFilterFields from "./CrmFilterFields.jsx";
import "../common/waypoint/waypoint.css";

/* The rows behind a CRM card, in the popup the card opens (CrmListModal)
   -- the page list the batch report draws (NewCustomDataTable in
   .td-list, a name over its detail in each cell, loaded by scrolling),
   which keeps the room a full row needs.
   The ROW is the control: it opens its student, trainer or batch in the
   view popup (Waypoint); a batch or trainer named inside a row is its own
   link to that one. The rows arrive filtered, ordered and worked out from
   GET /crm-reports/:kind/list; this draws them and asks for the next page
   as the page is scrolled. */

const PAGE = 25;

const pctOf = (v) => (v == null ? "—" : `${v}%`);
const starsOf = (v) => (v == null ? "—" : `${v.toFixed(1)} ★`);

const Cell = ({ name, sub, link }) => (
  <div className="lp-cell">
    {link ? (
      <button
        type="button"
        className="wp-link lp-name"
        // Its own control: the click must not also open the row.
        onClick={(e) => {
          e.stopPropagation();
          link();
        }}
      >
        {name}
      </button>
    ) : (
      <span className="lp-name">{name}</span>
    )}
    {sub && <span className="lp-sub">{sub}</span>}
  </div>
);

/* A figure in the colour of how it reads -- the tone arrives with the
   row (crmreport.service.js toneOfPct / toneOfRating), as the house pill.
   No tone (no figure, or too few responses to judge) stays plain. */
const Num = ({ value, tone }) =>
  tone ? <span className={`pg-pill pg-pill--${tone}`}>{value}</span> : <span className="lp-num">{value}</span>;

const Toned = ({ value, sub, tone }) => (
  <div className="lp-cell">
    <Num value={value} tone={tone} />
    {sub && <span className="lp-sub">{sub}</span>}
  </div>
);

/* Per report: the record a row opens, and its columns. */
const headersFor = (kind, open) => {
  if (kind === "students") {
    return [
      {
        title: "Student",
        prop: "studentName",
        cell: (r) => <Cell name={r.studentName} sub={[r.email, r.phone].filter(Boolean).join(" · ")} />,
      },
      {
        title: "Batch",
        prop: "batchName",
        cell: (r) => <Cell name={r.batchName} sub={r.trainerName} link={r.batchId ? () => open("batch", r.batchId) : null} />,
      },
      { title: "Attended", prop: "attended", cell: (r) => <Cell name={`${r.attended} of ${r.sessions}`} sub={`${r.absent} absent`} /> },
      { title: "Attendance", prop: "rate", cell: (r) => <Num value={pctOf(r.rate)} tone={r.rateTone} /> },
    ];
  }
  return [
    { title: "Batch", prop: "batchName", cell: (r) => <Cell name={r.batchName} sub={r.courseName} /> },
    {
      title: "Trainer",
      prop: "trainerName",
      cell: (r) => <Cell name={r.trainerName} link={r.trainerId ? () => open("trainer", r.trainerId) : null} />,
    },
    { title: "Students", prop: "students", cell: (r) => <Cell name={r.students} sub={`${r.sessions} sessions`} /> },
    { title: "Attendance", prop: "attendanceRate", cell: (r) => <Num value={pctOf(r.attendanceRate)} tone={r.attendanceTone} /> },
    {
      title: "Feedback",
      prop: "averageRating",
      cell: (r) => <Toned value={starsOf(r.averageRating)} sub={`${r.responses} responses`} tone={r.feedbackTone} />,
    },
    { title: "Score", prop: "score", cell: (r) => <Num value={r.score ?? "—"} tone={r.scoreTone} /> },
  ];
};

const OPENS = {
  students: (r) => ["student", r.studentId],
  batches: (r) => ["batch", r.batchId],
};

const NOUN = { students: ["enrolment", "enrolments"], batches: ["batch", "batches"] };

// `onOpen(type, id)` opens a record; the popup closes itself first.
// `filters` are the page's, as the popup opens; changed here, they apply
// to this list alone, as they change.
const CrmList = ({ kind, card, filters: pageFilters, onOpen }) => {
  const { open: waypointOpen } = useWaypoint();
  const openEntity = onOpen || waypointOpen;
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  const [filters, setFilters] = useState(pageFilters);
  const seq = useRef(0);
  const endRef = useRef(null);

  const applySearch = useDebouncedCallback((value) => setTerm(value.trim()), 350);

  // A new search or filter starts the list again from its first page.
  useEffect(() => {
    seq.current += 1;
    setRows([]);
    setPage(0);
    setHasMore(true);
    setTotal(null);
    setFailed(false);
    // A request still out for the old search is dropped on arrival, so
    // it must not hold the list in "loading" either.
    setLoading(false);
  }, [term, filters]);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore || failed) return;
    const mine = seq.current;
    const next = page + 1;
    setLoading(true);
    try {
      const res = await getCrmReportList(kind, { group: card.key, page: next, limit: PAGE, search: term, filters });
      if (mine !== seq.current) return;
      const got = res?.data?.rows || [];
      setRows((list) => [...list, ...got]);
      setPage(next);
      setTotal(res?.pagination?.total ?? null);
      setHasMore(next < (res?.pagination?.totalPages || 1));
    } catch (error) {
      if (mine === seq.current) setFailed(true);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [kind, card.key, term, filters, page, hasMore, loading, failed]);

  useScrollLoadMore(endRef, loadMore, [rows.length, term, filters, hasMore]);

  const headers = useMemo(() => headersFor(kind, openEntity), [kind, openEntity]);
  const [one, many] = NOUN[kind];
  const firstLoad = loading && rows.length === 0;

  return (
    <section aria-live="polite">
      <p className="br-dupes-note">
        {total == null ? "Loading..." : `${total} ${total === 1 ? one : many}`}
        {" · "}open a row to see it in full.
      </p>

      <div className="pg-filters-fields br-fields">
        <div className="pg-field">
          <label className="pg-label" htmlFor={`crm-list-${kind}-search`}>
            Search
          </label>
          <input
            id={`crm-list-${kind}-search`}
            type="search"
            className="pg-input pg-input--search"
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              applySearch(e.target.value);
            }}
          />
        </div>
        <CrmFilterFields kind={kind} value={filters} onChange={setFilters} idPrefix="crm-list" />
      </div>

      {failed ? (
        <p className="lp-muted">This list could not be loaded. Close it and try again.</p>
      ) : (
        <div className="td-list td-list--lms">
          <NewCustomDataTable
            data={rows}
            headers={headers}
            totalRecords={total || 0}
            currentPage={Math.max(page, 1)}
            recordsPerPage={PAGE}
            onPageChange={() => {}}
            loading={firstLoad}
            loadingMore={loading && rows.length > 0}
            serverSide={true}
            showToolbar={false}
            showPagination={false}
            showFooter={false}
            onRowClick={(row) => {
              const [type, id] = OPENS[kind](row);
              if (id) openEntity(type, id);
            }}
            validRowClickTagNames="*"
          />
        </div>
      )}

      <div className="att-picklist-foot" ref={endRef}>
        {loading && rows.length > 0 ? (
          <>
            <span className="pg-spinner att-spinner" aria-hidden="true" />
            <span>Loading more...</span>
          </>
        ) : !hasMore && rows.length > 0 ? (
          <span>All {total} shown.</span>
        ) : !loading && !hasMore && rows.length === 0 && !failed ? (
          <span>{term ? "Nothing matches that search." : "Nothing to list here."}</span>
        ) : null}
      </div>
    </section>
  );
};

export default CrmList;
